import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Portal,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  actionCountSx,
  closeButtonSx,
  closeIconSx,
  loadingIconSx,
  pillButtonSx,
  pillContainerSx,
  pillPositionSx,
  selectedCountBadgeSx,
  selectedCountContainerSx,
  selectedCountTextSx,
  verticalDividerSx,
} from './BulkActionPill.styles';

export interface PillActionButton {
  id: string;
  label: string;
  icon: React.ReactNode;
  count?: number;
  disabled?: boolean;
  disabledTitle?: string;
  loading?: boolean;
  onClick: () => void;
}

interface BulkActionPillProps {
  selectedCount: number;
  scopeLabel?: string;
  actions: PillActionButton[];
  onClear: () => void;
  centerX?: number | null;
}

const VerticalDivider: React.FC = () => <Box sx={verticalDividerSx} />;

const PillButton: React.FC<PillActionButton> = ({
  label,
  icon,
  count,
  disabled,
  disabledTitle,
  loading,
  onClick,
}) => {
  const button = (
    <Button
      onClick={onClick}
      disabled={disabled || loading}
      disableRipple
      sx={pillButtonSx}
      startIcon={loading ? <CircularProgress size={14} sx={loadingIconSx} /> : icon}
    >
      {label}
      {count != null && (
        <Box component="span" sx={actionCountSx}>
          {count}
        </Box>
      )}
    </Button>
  );

  if (disabled && disabledTitle) {
    return (
      <Tooltip title={disabledTitle}>
        <span>{button}</span>
      </Tooltip>
    );
  }
  return button;
};

const BulkActionPill: React.FC<BulkActionPillProps> = ({
  selectedCount,
  scopeLabel,
  actions,
  onClear,
  centerX,
}) => {
  const positionSx = React.useMemo(
    () => ({
      ...pillPositionSx,
      left: centerX != null ? `${centerX}px` : '50%',
    }),
    [centerX],
  );

  return (
    <Portal>
      <Box sx={positionSx}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={pillContainerSx}>
          <Stack direction="row" alignItems="center" spacing={1} sx={selectedCountContainerSx}>
            <Box sx={selectedCountBadgeSx}>{selectedCount}</Box>
            <Typography sx={selectedCountTextSx}>
              Selected{scopeLabel ? ` ${scopeLabel}` : ''}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={0.25}>
            {actions.map((action) => (
              <React.Fragment key={action.id}>
                {action.id === 'assign' && <VerticalDivider />}
                <PillButton {...action} />
              </React.Fragment>
            ))}
          </Stack>

          <VerticalDivider />

          <Tooltip title="Clear selection">
            <IconButton
              onClick={onClear}
              disableRipple
              sx={closeButtonSx}
              aria-label="Clear selection"
            >
              <CloseIcon sx={closeIconSx} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Portal>
  );
};

export default BulkActionPill;
